import { useState } from 'react';
import { FiBriefcase, FiFilter } from 'react-icons/fi';
import { IoIosRefresh } from 'react-icons/io';
import { FaPlus } from 'react-icons/fa6';
import { Outlet, useNavigate } from 'react-router-dom';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import PageFooter from '../../molecules/PageFooter';
import FilterPanel from './shared/FilterPanel';
import ActiveFilterChips from './shared/ActiveFilterChips';
import { deriveActiveFilters, LEGAL_CASE_FILTER_DEFS, buildLegalCaseFilterDefs } from './shared/filterTypes';
import { formatDate } from '../../../utils/collateralFormatters';
import useLegalCasePageList from '../../../hooks/useLegalCasePageList';
import useBeneficiaries from '../../../hooks/useBeneficiaries';
import { type LegalCaseListItem, type SelectOption } from '../../../types/LegalCase';

// Valor "desligado" de um filtro por id — toggle volta a false, os demais a ''. Usado ao
// remover um chip. Fora do componente (diretriz: sem função dentro de função).
const clearedValue = (id: string): string | boolean => {
    const def = LEGAL_CASE_FILTER_DEFS.find((d) => d.id === id);
    return def?.kind === 'toggle' ? false : '';
};

const columns = [
    { id: 'externalCaseNumber', title: 'Processo', field: 'externalCaseNumber', canSort: true, width: '170px' },
    { id: 'caseType', title: 'Tipo', field: 'caseType', canSort: false, width: '150px' },
    { id: 'pole', title: 'Polo', field: 'pole', canSort: false, width: '90px', cssClass: 'text-center' },
    { id: 'creditorEntity', title: 'Entidade credora', field: 'creditorEntity', canSort: true, width: '190px' },
    { id: 'court', title: 'Foro / Comarca', field: 'court', canSort: false, width: '190px' },
    { id: 'status', title: 'Situação', field: 'status', canSort: false, width: '110px', cssClass: 'text-center' },
    { id: 'responsibleFirm', title: 'Escritório', field: 'responsibleFirm', canSort: false, width: '170px' },
    { id: 'nextDeadline', title: 'Próximo prazo', field: 'nextDeadline', canSort: false, width: '130px', cssClass: 'text-center' },
    { id: 'pendingMeasures', title: 'Medidas pendentes', field: 'pendingMeasures', canSort: false, width: '180px' },
    { id: 'openedAt', title: 'Distribuição', field: 'openedAt', canSort: true, width: '110px', cssClass: 'text-center' },
];

// Célula "Próximo prazo": data do vencimento mais próximo não cumprido, em vermelho se já
// vencido. Extraída para fora do componente (diretriz: sem função dentro de função).
const NextDeadlineCell = ({ row }: { row: LegalCaseListItem }) => {
    if (!row.nextDeadlineDate) return <span className="text-gray-400 dark:text-gray-500">—</span>;
    return (
        <span className={row.nextDeadlineOverdue ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-600 dark:text-gray-300'}>
            {formatDate(row.nextDeadlineDate)}
        </span>
    );
};

// Célula "Medidas pendentes": chips dos tipos em aberto (até 2) + contador do excedente.
const PendingMeasuresCell = ({ row }: { row: LegalCaseListItem }) => {
    if (row.pendingMeasureCount === 0) return <span className="text-gray-400 dark:text-gray-500">—</span>;
    const shown = row.pendingMeasureTypesFormatted.slice(0, 2);
    const extra = row.pendingMeasureTypesFormatted.length - shown.length;
    return (
        <div className="flex flex-wrap gap-1">
            {shown.map((t) => (
                <span key={t} className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300">
                    {t}
                </span>
            ))}
            {extra > 0 && (
                <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                    +{extra}
                </span>
            )}
        </div>
    );
};

const LegalCasePageList = () => {
    const navigate = useNavigate();
    const {
        rows, total, page, setPage, size,
        isLoading, initialLoading,
        searchInput, setSearchInput, setSearch,
        sort, setSort,
        filterValues, setFilterValue, clearFilters,
        refresh, handleRowClick,
        apiInfo,
    } = useLegalCasePageList();

    const [filtersOpen, setFiltersOpen] = useState(false);

    // Escritórios cadastrados para o filtro "escritório responsável" no drawer.
    const beneficiaries = useBeneficiaries();
    const firmOptions: SelectOption[] = beneficiaries.beneficiaries
        .filter((b) => b.beneficiaryType === 'LAW_FIRM')
        .map((b) => ({ value: b.id, label: b.name }));
    const filterDefs = buildLegalCaseFilterDefs(firmOptions);

    const activeFilters = deriveActiveFilters(filterDefs, filterValues);

    const onSort = (field: string, order: 'ASC' | 'DESC') => {
        setSort(order === 'DESC' ? `-${field}` : field);
    };
    const currentField = sort.startsWith('-') ? sort.slice(1) : sort;
    const currentOrder: 'ASC' | 'DESC' = sort.startsWith('-') ? 'DESC' : 'ASC';

    return (
        <>
            {isLoading && <Loading />}

            <div className="flex flex-col h-screen bg-white dark:bg-slate-800 overflow-hidden">
                {/* Cabeçalho */}
                <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <FiBriefcase className="text-blue-600" size={20} />
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Casos jurídicos</h1>
                            {apiInfo.environment && (
                                <span className={`text-xs font-medium px-2 py-0.5 rounded ${apiInfo.environment === 'prd' ? 'text-red-600' : apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                                    {apiInfo.environment.toUpperCase()}{apiInfo.version ? ` v${apiInfo.version}` : ''}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-72">
                                <TextEdit label="Buscar processo, entidade, foro..." value={searchInput}
                                    onChange={(e: any) => setSearchInput(e.target.value)}
                                    onKeyDown={(e: any) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }} />
                            </div>
                            <button onClick={() => setFiltersOpen(true)}
                                className="relative flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-sm font-medium"
                                title="Filtros">
                                <FiFilter size={14} /> Filtros
                                {activeFilters.length > 0 && (
                                    <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-blue-600 text-white">
                                        {activeFilters.length}
                                    </span>
                                )}
                            </button>
                            <button onClick={refresh}
                                className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                title="Atualizar lista">
                                <IoIosRefresh size={18} />
                            </button>
                            <button onClick={() => navigate('/recovery/legal-cases/new')}
                                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                                <FaPlus size={12} /> Novo
                            </button>
                        </div>
                    </div>
                </div>

                {/* Chips dos filtros ativos (oculto quando não há filtro) */}
                <ActiveFilterChips
                    filters={activeFilters}
                    onRemove={(id) => setFilterValue(id, clearedValue(id))}
                    onClearAll={clearFilters}
                />

                {/* Grid full-width */}
                <div className="flex-1 overflow-auto [&_table]:table-fixed [&_table]:w-full">
                    {initialLoading ? (
                        <TableSkeleton rows={12} />
                    ) : (
                        <ATable headers={columns} fieldSorted={currentField} orderSorted={currentOrder} onSort={onSort}>
                            {rows.length === 0 ? (
                                <tr><td colSpan={columns.length} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhum caso encontrado</td></tr>
                            ) : rows.map((row: LegalCaseListItem, index: number) => (
                                <tr key={row.id}
                                    className={`group hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}
                                    onClick={() => handleRowClick(row)}>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm font-mono text-blue-700 dark:text-blue-400 truncate">{row.externalCaseNumber || '—'}</td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300">{row.caseTypeFormatted}</td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300">
                                            {row.poleFormatted}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white truncate" title={row.creditorEntity}>{row.creditorEntity}</td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300 truncate">
                                        {row.court || '—'}{row.district ? ` • ${row.district}` : ''}
                                    </td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${row.status === 'OPEN'
                                            ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                                            : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300'}`}>
                                            {row.statusFormatted}
                                        </span>
                                    </td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300 truncate" title={row.responsibleFirmName || ''}>{row.responsibleFirmName || '—'}</td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm text-center">
                                        <NextDeadlineCell row={row} />
                                    </td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm">
                                        <PendingMeasuresCell row={row} />
                                    </td>
                                    <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm text-gray-500 dark:text-gray-400 text-center">{row.openedAt ? formatDate(row.openedAt) : '—'}</td>
                                </tr>
                            ))}
                        </ATable>
                    )}
                </div>

                {/* Rodapé / paginação */}
                <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                    <PageFooter currentPage={page - 1} totalPages={Math.ceil(total / size)} itemsPerPage={size} totalElements={total} onPageChange={(p: number) => setPage(p + 1)} />
                </div>
            </div>

            <FilterPanel
                open={filtersOpen}
                defs={filterDefs}
                values={filterValues}
                onChange={setFilterValue}
                onClearAll={clearFilters}
                onClose={() => setFiltersOpen(false)}
            />

            <Outlet context={{ refresh }} />
        </>
    );
};

export default LegalCasePageList;
